import React, { useState, useCallback, useEffect, useRef } from "react";
import Dropzone from "react-dropzone";
import UploadService from "../services/upload-files.service";
import { Spinner, Alert, ProgressBar, Badge, Modal, Form, Button } from "react-bootstrap";
import './UploadFiles.css';

const UploadFilesTreading = ({ onUploadComplete, onCancel, onUploadingStatusChange, projectId }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [fileStatuses, setFileStatuses] = useState({});
  const [overallProgress, setOverallProgress] = useState(0);
  const [duplicateFile, setDuplicateFile] = useState(null);
  const [newFilename, setNewFilename] = useState('');
  // Use refs to track polling intervals and active file IDs
  const pollIntervalsRef = useRef({});
  const activeFileIdsRef = useRef([]);

  const org = localStorage.getItem('org') || "";
  const MAX_FILE_SIZE = (org === "") ? 10 * 1024 * 1024 : 200 * 1024 * 1024;
  const MAX_FILES = (org === "") ? 5 : 20;

  const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/rtf',
    'text/rtf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain'
  ];
  const ALLOWED_FILE_EXTENSIONS = ['.pdf', '.doc', '.docx', '.rtf', '.xls', '.xlsx', '.txt'];

  // Clear all intervals when unmounting
  useEffect(() => {
    return () => {
      Object.values(pollIntervalsRef.current).forEach(interval => {
        clearInterval(interval);
      });
    };
  }, []);

  // Calculate overall progress whenever file statuses change
  useEffect(() => {
    if (Object.keys(fileStatuses).length === 0) return;

    // Calculate overall progress (only include non-duplicate files)
    const validStatuses = Object.values(fileStatuses).filter(
      status => status.status !== 'duplicate' && typeof status.progress === 'number'
    );
    
    if (validStatuses.length === 0) return;

    const totalProgress = validStatuses.reduce((sum, status) => sum + (status.progress || 0), 0);
    const avgProgress = totalProgress / validStatuses.length;
    setOverallProgress(Math.round(avgProgress));

    // Check if all files are complete
    const allComplete = Object.values(fileStatuses).every(
      status => status.status === 'completed' || status.status === 'error'
    );

    if (allComplete && isLoading) {
      setIsLoading(false);
      if (onUploadingStatusChange) onUploadingStatusChange(false);

      // Check if any files had errors
      const hasErrors = Object.values(fileStatuses).some(status => status.status === 'error');

      if (!hasErrors) {
        setTimeout(() => {
          if (onUploadComplete) {
            onUploadComplete();
          }
        }, 1500);
      }
    }
  }, [fileStatuses, isLoading, onUploadComplete, onUploadingStatusChange]);

  const validateFile = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      return `${file.name} is too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB.`;
    }
    const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
    if (!ALLOWED_FILE_EXTENSIONS.includes(fileExtension)) {
      return `${file.name} is not a supported file type. Please upload PDF, DOC, DOCX, RTF, XLS, XLSX, or TXT files.`;
    }

    if (!ALLOWED_FILE_TYPES.includes(file.type) && file.type !== '') {
      console.warn(`File ${file.name} has unrecognized MIME type: ${file.type}`);
    }

    return null;
  };

  const onDrop = useCallback((acceptedFiles) => {
    const validFiles = [];
    const errorMessages = [];
    let totalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);

    acceptedFiles.forEach(file => {
      if (validFiles.length + selectedFiles.length >= MAX_FILES) {
        errorMessages.push(`Maximum number of files (${MAX_FILES}) reached.`);
        return;
      }

      const error = validateFile(file);
      if (error) {
        errorMessages.push(error);
      } else if (totalSize + file.size > MAX_FILE_SIZE) {
        errorMessages.push(`Adding ${file.name} would exceed the total size limit of ${MAX_FILE_SIZE / (1024 * 1024)}MB.`);
      } else {
        validFiles.push(file);
        totalSize += file.size;
      }
    });

    setSelectedFiles(prevFiles => [...prevFiles, ...validFiles]);
    if (errorMessages.length > 0) {
      setErrors(errorMessages);
    }
  }, [selectedFiles]);

  // Start polling for a file's status
  const startPolling = (fileId, fileName) => {
    console.log(`Starting to poll status for ${fileName} (ID: ${fileId})`);

    // Make sure fileId is valid
    if (!fileId || typeof fileId !== 'string') {
      console.error(`Invalid fileId: ${fileId}`);
      return;
    }

    // Clear existing interval if any
    if (pollIntervalsRef.current[fileId]) {
      clearInterval(pollIntervalsRef.current[fileId]);
    }

    // Create new polling interval
    const intervalId = setInterval(async () => {
      try {
        // Use your API endpoint for file status
        const response = await UploadService.getFileStatus(fileId);

        if (response?.data) {
          const { status, progress, message } = response.data;

          console.log(`Status update for ${fileName}: ${status}, progress: ${progress}%, message: ${message}`);

          // Update this file's status
          setFileStatuses(prev => ({
            ...prev,
            [fileId]: {
              ...prev[fileId],
              fileName,
              status,
              progress: parseInt(progress) || 0,
              message: message || status
            }
          }));

          // Stop polling if complete
          if (status === 'completed' || status === 'error') {
            console.log(`File ${fileName} processing ${status}. Stopping polling.`);
            clearInterval(pollIntervalsRef.current[fileId]);
            delete pollIntervalsRef.current[fileId];

            // Remove from active IDs
            activeFileIdsRef.current = activeFileIdsRef.current.filter(id => id !== fileId);

            if (status === 'error') {
              setErrors(prev => [...prev, `Error processing ${fileName}: ${message || "Unknown error"}`]);
            }
          }
        }
      } catch (error) {
        console.error(`Error polling status for ${fileName}:`, error);
      }
    }, 2000);

    // Store the interval ID
    pollIntervalsRef.current[fileId] = intervalId;

    // Add to active file IDs
    activeFileIdsRef.current.push(fileId);
  };

  
  const uploadSingleFile = async (file) => {
    // Create unique key for this file
    const fileKey = `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Add this file to status tracking
    setFileStatuses(prev => ({
      ...prev,
      [fileKey]: {
        fileName: file.name,
        status: 'checking',
        progress: 0,
        message: 'Checking for duplicates...'
      }
    }));

    try {

      // Try to check for duplicates first
      const checkResponse = await UploadService.checkDuplicate(file, projectId);
      console.log("Check duplicate response:", checkResponse);

      if (checkResponse?.data?.status === 'duplicate_found') {
        // Show duplicate dialog
        setFileStatuses(prev => ({
          ...prev,
          [fileKey]: {
            ...prev[fileKey],
            status: 'duplicate',
            message: 'Duplicate file found'
          }
        }));

        // Set the duplicate info
        setDuplicateFile({
          fileKey: fileKey,
          filename: checkResponse.data.filename,
          tempPath: checkResponse.data.temp_path,
          duplicates: checkResponse.data.duplicates || []
        });

        // Set default new filename
        const nameParts = file.name.split('.');
        const ext = nameParts.pop();
        const baseName = nameParts.join('.');
        setNewFilename(`${baseName}_${Date.now()}.${ext}`);

        return false; // Stop here until user makes a choice
      }


      // If we get here, either there's no duplicate or checking failed
      // Continue with upload
      setFileStatuses(prev => ({
        ...prev,
        [fileKey]: {
          ...prev[fileKey],
          status: 'vectoring',
          progress: 0,
          message: 'Starting vector...'
        }
      }));

      // Upload with progress tracking
      const response = await UploadService.uploadThreading(file, projectId, (event) => {
        const uploadProgress = Math.round((event.loaded * 10) / event.total);

        // Update progress for this file
        setFileStatuses(prev => ({
          ...prev,
          [fileKey]: {
            ...prev[fileKey],
            progress: uploadProgress,
            message: `Vectoring: ${uploadProgress}%`
          }
        }));
      });

      // Get file ID from response
      let fileId = response?.data?.file_id;

      if (!fileId) {
        // Check if the response indicates a duplicate
        if (response?.data?.status === 'duplicate_found') {
          // Handle duplicate case
          setFileStatuses(prev => ({
            ...prev,
            [fileKey]: {
              ...prev[fileKey],
              status: 'duplicate',
              message: 'Duplicate file found'
            }
          }));

          // Set duplicate info
          setDuplicateFile({
            originalFile: file,
            fileKey: fileKey,
            filename: response.data.filename,
            duplicates: response.data.duplicates || []
          });

          // Set default new filename
          const nameParts = file.name.split('.');
          const ext = nameParts.pop();
          const baseName = nameParts.join('.');
          setNewFilename(`${baseName}_${Date.now()}.${ext}`);

          return false;
        } else {
          throw new Error("Server did not return a file ID");
        }
      }

      // Create new entry with real file ID
      setFileStatuses(prev => {
        const newStatuses = { ...prev };
        delete newStatuses[fileKey]; // Remove temp key

        return {
          ...newStatuses,
          [fileId]: {
            fileName: file.name,
            status: 'processing',
            progress: 10,
            message: 'Processing started...'
          }
        };
      });

      // Start polling for status updates
      startPolling(fileId, file.name);

      return true;
    } catch (error) {
      console.error(`Error vectoring ${file.name}:`, error);

      // Update status to error
      setFileStatuses(prev => ({
        ...prev,
        [fileKey]: {
          ...prev[fileKey],
          status: 'error',
          progress: 0,
          message: `Error: ${error.message || "Vector failed"}`
        }
      }));

      setErrors(prev => [...prev, `Failed to vector ${file.name}: ${error.message || "Unknown error"}`]);
      return false;
    }
  };

  // Upload all selected files
  const upload = async () => {
    if (selectedFiles.length === 0) return;

    setIsLoading(true);
    if (onUploadingStatusChange) onUploadingStatusChange(true);
    setErrors([]);
    setFileStatuses({});
    setOverallProgress(0);

    // Upload files one by one
    for (const file of selectedFiles) {
      try {
        await uploadSingleFile(file);
      } catch (error) {
        console.error(`Error processing ${file.name}:`, error);
        // Continue with next file
      }
    }
  };

  // Handle cancel button
  const handleCancel = () => {
    if (isLoading) {
      if (window.confirm("Vector is in progress. The vector will continue in the background, but you won't see its progress. Are you sure you want to close this window?")) {
        // Clear all polling intervals
        Object.values(pollIntervalsRef.current).forEach(interval => {
          clearInterval(interval);
        });
        pollIntervalsRef.current = {};
        activeFileIdsRef.current = [];

        setIsLoading(false);
        if (onUploadingStatusChange) onUploadingStatusChange(false);
        onCancel();
      }
    } else {
      onCancel();
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };


  // Update the handleRename function
const handleRename = async () => {
  if (!duplicateFile || !newFilename) return;
  
  const { fileKey, tempPath, filename } = duplicateFile;
  
  setFileStatuses(prev => ({
    ...prev,
    [fileKey]: {
      ...prev[fileKey],
      status: 'vectording',
      message: 'Vectoring with new name...'
    }
  }));
  
  try {
    const response = await UploadService.renameAndUpload(
      tempPath, 
      newFilename, 
      projectId
    );
    
    if (response?.data?.file_id) {
      const fileId = response.data.file_id;
      
      setFileStatuses(prev => {
        const newStatuses = { ...prev };
        delete newStatuses[fileKey];
        
        return {
          ...newStatuses,
          [fileId]: {
            fileName: newFilename,
            status: 'processing',
            progress: 10,
            message: 'Processing with new name...'
          }
        };
      });
      
      startPolling(fileId, newFilename);
    } else {
      throw new Error("Failed to upload with new name");
    }
    
    setDuplicateFile(null);
  } catch (error) {
    console.error("Error renaming file:", error);
    
    setFileStatuses(prev => ({
      ...prev,
      [fileKey]: {
        ...prev[fileKey],
        status: 'error',
        message: `Error: ${error.message || "Rename failed"}`
      }
    }));
    
    setErrors(prev => [...prev, `Failed to rename file: ${error.message || "Unknown error"}`]);
    setDuplicateFile(null);
  }
};

// Update the handleOverwrite function
const handleOverwrite = async () => {
  if (!duplicateFile) return;
  
  const { fileKey, tempPath, filename } = duplicateFile;
  
  setFileStatuses(prev => ({
    ...prev,
    [fileKey]: {
      ...prev[fileKey],
      status: 'uploading',
      message: 'Overwriting existing file...'
    }
  }));
  
  try {
    const response = await UploadService.overwriteAndUpload(
      tempPath, 
      filename, 
      projectId
    );
    
    if (response?.data?.file_id) {
      const fileId = response.data.file_id;
      
      setFileStatuses(prev => {
        const newStatuses = { ...prev };
        delete newStatuses[fileKey];
        
        return {
          ...newStatuses,
          [fileId]: {
            fileName: filename,
            status: 'processing',
            progress: 10,
            message: 'Processing after overwrite...'
          }
        };
      });
      
      startPolling(fileId, filename);
    } else {
      throw new Error("Failed to overwrite file");
    }
    
    setDuplicateFile(null);
  } catch (error) {
    console.error("Error overwriting file:", error);
    
    setFileStatuses(prev => ({
      ...prev,
      [fileKey]: {
        ...prev[fileKey],
        status: 'error',
        message: `Error: ${error.message || "Overwrite failed"}`
      }
    }));
    
    setErrors(prev => [...prev, `Failed to overwrite file: ${error.message || "Unknown error"}`]);
    setDuplicateFile(null);
  }
};

const handleCancelDuplicate = () => {
  // Get the fileKey from the duplicate that's being canceled
  const { fileKey } = duplicateFile;
  
  // Remove this file status from tracking
  setFileStatuses(prev => {
    const newStatuses = { ...prev };
    delete newStatuses[fileKey];
    return newStatuses;
  });
  
  // Clear the duplicate state
  setDuplicateFile(null);

  // Check if there are any remaining files being processed
  const remainingFiles = Object.values(fileStatuses).filter(
    status => status.status !== 'duplicate' && status.status !== 'error' && status.status !== 'completed'
  );
  
  // If no files are being processed, set isLoading to false
  if (remainingFiles.length === 0) {
    setIsLoading(false);
    if (onUploadingStatusChange) onUploadingStatusChange(false);
  }
};
  // Add this to your render function
  // Duplicate file dialog
  const renderDuplicateDialog = () => {
    if (!duplicateFile) return null;

    return (
      <Modal 
        show={true} 
        onHide={handleCancelDuplicate}
        backdrop="static" // This prevents closing when clicking outside
        keyboard={false} // This prevents closing with the Esc key
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Duplicate File</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            A file with the name <strong>{duplicateFile.filename}</strong> already exists.
            What would you like to do?
          </p>

          {duplicateFile.duplicates && duplicateFile.duplicates.length > 0 && (
            <div className="mb-3">
              <strong>Existing files:</strong>
              <ul className="list-group mt-2">
                {duplicateFile.duplicates.map((dup, index) => (
                  <li key={index} className="list-group-item">
                    <div>{dup.name}</div>
                    <small className="text-muted">
                      {dup.visibility === 'global' ? 'Global file' : 'Your file'} -
                      Vectored {new Date(dup.uploaded_at).toLocaleString()}
                    </small>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Rename to:</Form.Label>
            <Form.Control
              type="text"
              value={newFilename}
              onChange={(e) => setNewFilename(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCancelDuplicate}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleOverwrite}
          >
            Overwrite
          </Button>
          <Button
            variant="primary"
            onClick={handleRename}
            disabled={!newFilename}
          >
            Rename and Vector
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };


  return (
    <div>
      {/* Dropzone */}
      <Dropzone onDrop={onDrop} multiple={true} disabled={isLoading}>
        {({ getRootProps, getInputProps }) => (
          <section>
            <div {...getRootProps({ className: `dropzone ${isLoading ? 'disabled' : ''}` })}>
              <input {...getInputProps()} />
              {selectedFiles.length > 0 ? (
                <div className="selected-files-container">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="selected-file">
                      <span className="file-name">{file.name}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                        className="btn btn-link delete-btn"
                        disabled={isLoading}
                      >
                        <i className="bi bi-x-circle"></i>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p>{isLoading ? "Vector in progress..." : "Drag and drop files here, or click to select files"}</p>
              )}
            </div>
          </section>
        )}
      </Dropzone>

      {/* Progress indicators */}
      {isLoading && (
        <div>
          {selectedFiles.length === 1 ? (
            // Single file upload UI
            <div className="mt-3">
              <ProgressBar
                now={Object.values(fileStatuses)[0]?.progress || 0}
                label={`${Object.values(fileStatuses)[0]?.progress || 0}%`}
                variant="info"
                animated
              />
              <p className="text-center mt-2">
                {Object.values(fileStatuses)[0]?.message || "Processing... Please wait."}
              </p>
              <div className="text-center">
                <Spinner animation="border" role="status" size="sm" className="me-2">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                {Object.values(fileStatuses)[0]?.status || "Processing..."}
              </div>
            </div>
          ) : (
            // Multiple files upload UI
            <div>
              <div className="mt-3">
                <ProgressBar
                  now={overallProgress}
                  label={`${overallProgress}%`}
                  variant={overallProgress < 100 ? "info" : "success"}
                  animated={overallProgress < 100}
                />
                <p className="text-center mt-1">Overall Progress</p>
              </div>

              <div className="file-status-list mt-3">
                {Object.entries(fileStatuses).map(([fileId, status]) => (
                  <div key={fileId} className="file-status-item mb-2">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="file-name">{status.fileName}</span>
                      <Badge bg={
                        status.status === 'completed' ? 'success' :
                          status.status === 'error' ? 'danger' : 'info'
                      }>
                        {status.status}
                      </Badge>
                    </div>
                    <ProgressBar
                      now={status.progress}
                      variant={
                        status.status === 'completed' ? 'success' :
                          status.status === 'error' ? 'danger' : 'info'
                      }
                      animated={status.status !== 'completed' && status.status !== 'error'}
                      style={{ height: '0.5rem' }}
                    />
                    <small className="text-muted">{status.message}</small>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error messages */}
      {errors.length > 0 && (
        <Alert variant="danger" className="mt-3">
          <ul className="mb-0">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Footer and buttons */}
      <p className="text-muted">You may select up to {MAX_FILES} files, or a maximum of {MAX_FILE_SIZE / (1024 * 1024)} MB for
        all selected files, to batch "Vector"</p>
      <div className="mt-3">
        <button
          className="btn btn-secondary me-2"
          onClick={handleCancel}
          disabled={isLoading && Object.keys(pollIntervalsRef.current).length > 0}
        >
          Cancel
        </button>

        <button
          className="btn btn-primary me-2"
          disabled={selectedFiles.length === 0 || isLoading}
          onClick={upload}
        >
          Vector
        </button>
      </div>

      {renderDuplicateDialog()}
    </div>
  );


};

export default UploadFilesTreading;